<template>
    <v-app>
        <router-view v-if="['login-azure', 'login-pin', 'admin-access', 'unauthorised'].indexOf($route.name) > -1"></router-view>
        <div v-else-if="hasLoaded">
            <v-app-bar :clipped-left="$vuetify.breakpoint.lgAndUp"
                       :clipped-right="$vuetify.breakpoint.lgAndUp"
                       color="secondary"
                       dark
                       app
                       fixed>
                <v-toolbar-title>
                    <span class="text-h6" style="line-height:2.5em;">{{ applicationTitle }}</span>
                    <beta-chip></beta-chip>
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <v-toolbar-items>
                    <user-menu></user-menu>
                </v-toolbar-items>
            </v-app-bar>
            <v-main>
                <v-container fluid>
                    <v-slide-y-transition mode="out-in">
                        <v-row>
                            <v-col class="mx-2">
                                <router-view></router-view>
                            </v-col>
                        </v-row>
                    </v-slide-y-transition>
                </v-container>
            </v-main>
        </div>
    </v-app>
</template>

<script>
    import Vue from 'vue';
    import { eventBus } from '@/main'
    import { PublicClientApplication } from '@azure/msal-browser';
    import is from 'is_js'

    import Toaster from 'v-toaster';
    import 'v-toaster/dist/v-toaster.css';
    Vue.use(Toaster, { timeout: 5000 });

    import ScrollTo from 'vue-scroll-to';
    Vue.use(ScrollTo, { speed: 250 });

    import globalMixin from '@/views/components/mixins/base/global.js'
    import xsrfMixin from '@/views/components/mixins/base/xsrf.js'
    import assetsMixin from '@/views/components/mixins/base/assets.js'
    import azureAuthMixin from '@/views/components/mixins/base/azureAuth.js'
    import studentsMixin from '@/views/components/mixins/enterpriseDataApi/students.js'
    import appApiMixin from '@/views/components/mixins/appApi.js'

    import BetaChip from '@/views/components/toolbar/BetaChip'
    import UserMenu from '@/views/components/toolbar/UserMenu'

    export default {
        components: {
            'beta-chip': BetaChip,
            'user-menu': UserMenu,
        },
        mixins: [
            globalMixin,
            xsrfMixin,
            assetsMixin,
            azureAuthMixin,
            studentsMixin,
            appApiMixin,
        ],
        data() {
            return {
                hasLoaded: false,
                appTokenIntervalID: null,
                accessTokenIntervalID: null,
            }
        },
        async mounted() {
            this.InitialiseErrorAndConsole();

            await this.SetAuthority();
            await this.SetClientID();

            if (this.instance == null) {
                this.config.auth.redirectUri = process.env.BASE_URL;
                this.config.auth.postLogoutRedirectUri = process.env.BASE_URL + 'login';

                let instance = new PublicClientApplication(this.config);
                this.SetInstance(instance);
            }

            if (is.ie() || is.edge()) window.location.href = process.env.BASE_URL + 'Browser/Index';
            else {
                eventBus.$off('sign-in:success').$on('sign-in:success', account => this.GetAccessToken(account));
                eventBus.$off('access-token-fetch:success').$on('access-token-fetch:success', () => this.LoadAppTokenByAccessToken(sessionStorage.studentID));
                eventBus.$off('access-token-fetch:failed').$on('access-token-fetch:failed', () => this.ForceReLogin());
                eventBus.$off('app-token-fetch:started').$on('app-token-fetch:started', () => this.loadingMessage = 'Connecting to services...');
                eventBus.$off('app-token-fetch:success').$on('app-token-fetch:success', () => {
                    this.appTokenIntervalID = setInterval(() => this.ReacquireAppToken(), 10000);
                    sessionStorage.removeItem('studentID');
                    if (!this.IsAuthorised) this.$router.push({ path: '/error/unauthorised' });
                    else this.$router.replace({ path: '/' }).catch(() => { });
                });
                eventBus.$off('app-token-fetch:finished').$on('app-token-fetch:finished', () => {
                    this.UpdateLoadedFlag();
                    this.accessTokenIntervalID = setInterval(() => this.ReacquireAccessToken(), 10000);
                });
                eventBus.$on('sign-in:clear-login', () => this.ClearLogin());

                this.UpdateLoadedFlag();

                // User is returning from sign in redirect
                if (sessionStorage.getItem(`msal.interaction.status`) == this.config.auth.clientId) this.ForceReLogin();

                else if (!this.hasLoaded) {
                    // If user is not logged in, and the current route is not a login page (to allow for pin and admin logins)...
                    if (['login-azure', 'login-pin'].indexOf(this.$route.name) == -1) {
                        if (this.$route.name == 'admin-access') {
                            let studentId = this.$route.params.studentId;

                            if (!isNaN(parseFloat(studentId)) && isFinite(studentId)) {
                                sessionStorage.studentID = parseInt(this.$route.params.studentId);
                                this.SignIn();
                            }
                            else eventBus.$emit('sign-in:clear-login');
                        }
                        else {
                            // ...redirect to the standard login page
                            switch (sessionStorage.loginMethod) {
                                case 'pin': this.$router.push({ name: 'login-pin' }); break;
                                default: this.$router.push({ name: 'login-azure' }); break;
                            }
                        }
                    }
                }
                else {
                    this.$router.beforeEach(async (to, from, next) => {
                        if (!this.IsAuthorised && to.name !== 'unauthorised-error') return '/error/unauthorised';
                        next();
                        window.scrollTo(0, 0);
                    });
                }
            }
        },
        methods: {
            InitialiseErrorAndConsole() {
                eventBus.$on('alert:success', response => this.$toaster.success(response.message));
                eventBus.$on('alert:error', error => {
                    try { this.$toaster.error(JSON.parse(error)?.Message); }
                    catch (ex) { this.$toaster.error(error); }
                });

                eventBus.$on('console:error', error => {
                    try { console.error(JSON.parse(error)?.Message); }
                    catch (ex) { console.error(error); }
                });
            },
            UpdateLoadedFlag() {
                this.hasLoaded = !!sessionStorage.appToken
                    && !!sessionStorage.loginMethod;
            },
            ClearLogin() {
                sessionStorage.removeItem('appToken');
                sessionStorage.loginMethod = 'accessToken';
                this.hasLoaded = false;
                this.$router.push({ path: '/login' });
            },
            ForceReLogin() {
                sessionStorage.removeItem('appToken');
                sessionStorage.removeItem('accessToken');
                this.ClearAuth();

                this.SignIn();
            }
        },
    }
</script>
